import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faHouse,
	faRulerVertical,
	faPersonSwimming,
	faWarehouse,
	faElevator,
	faSolarPanel,
	faVectorSquare,
	faRoad,
	faShieldHalved
} from '@fortawesome/free-solid-svg-icons';
import styles from './Kompleks.module.css';
import Contact from '../ui/Contact.js';

import birdseye from '../assets/webp/birdseye.webp';
import air2 from '../assets/webp/Aerial_2_Night_7.2.webp';

function Kompleks({ languageEn }) {
	const text = {
		en: {
			title: 'Gated Community of Houses in Sofia — The Vkushty Complex, Bistritsa',
			metaDescription:
				'A new gated community of houses in Sofia: the Vkushty complex is four houses of 640-780 sq.m on Izgled Street, Bistritsa. Controlled access, alarm system, cameras and perimeter sensors, own plot per house, 5 minutes from the ring road.',
			h1: 'The Vkushty Complex',
			subtitle: 'A gated community of just four houses above Sofia',
			intro:
				'The Vkushty complex is a gated community of four contemporary houses at the top of Izgled Street in Bistritsa, Sofia. Each house is 640-780 sq.m across three levels, on its own plot of 920-980 sq.m, behind a single controlled entrance. It is a boutique gated complex rather than a development — four neighbours, one street, and a 360° panorama over the city.',
			heroAlt: 'Aerial view of the Vkushty gated community of four houses in Bistritsa, Sofia',
			factsTitle: 'The complex at a glance',
			facts: [
				{ icon: faHouse, text: 'Only 4 houses in the whole complex' },
				{ icon: faShieldHalved, text: 'Gated, with alarm system, cameras and perimeter sensors' },
				{ icon: faVectorSquare, text: 'Own plot of 920-980 sq.m per house' },
				{ icon: faRulerVertical, text: '640-780 sq.m on three levels, 3.39 m clear ceilings' },
				{ icon: faPersonSwimming, text: 'Heated pool in every house' },
				{ icon: faWarehouse, text: '4-car garage per house' },
				{ icon: faElevator, text: 'KONE elevator' },
				{ icon: faSolarPanel, text: 'Photovoltaics and heat pumps' },
				{ icon: faRoad, text: '5 minutes from the Sofia ring road' }
			],
			whyTitle: 'What a gated community of houses actually gives you',
			whyParagraphs: [
				'Most gated complexes in Sofia are apartment buildings with a barrier at the entrance. A gated community of houses is a different proposition: no shared walls, no shared entrance hall, no lift you queue for. What is shared is only the things worth sharing — the access control, the perimeter, the maintenance of the street.',
				'Scale matters as much as the gate. A complex of four houses means four owners, not forty — decisions about the shared parts are a conversation rather than a general assembly, and the street stays quiet because almost nobody has reason to drive down it.',
				'Security is layered rather than symbolic: controlled access at the entrance, an alarm system, cameras and perimeter sensors, with each house sitting inside its own fenced plot behind all of it.'
			],
			housesTitle: 'The four houses',
			housesParagraphs: [
				'Each house is arranged over three levels. Level -1 holds the garages, service and wet rooms and spacious wardrobes. The main floor is the active part of the home — a spacious living room, kitchen, office and fitness area. The upper floor is for rest: a master bedroom with a large dressing room, plus the children\'s rooms.',
				'The construction is new, with suspended aluminium facades by Schüco of Germany and 3.39 m clear ceiling heights. The glass surfaces are what the design is built around: natural light through the day, and the view over Sofia from essentially every room.',
				'Each house has its own heated pool, a rooftop photovoltaic system and heat pumps, on a plot of 920-980 sq.m.'
			],
			whereTitle: 'Where the complex is',
			whereParagraphs: [
				'Izgled Street sits at the top of Bistritsa, at 897 m on the southern slopes of Vitosha — above the winter inversion that holds smog over most of Sofia from November to March. The panorama runs east and north over the whole city, west and south into the mountain.',
				'It is still only 5 minutes to the ring road, which is the balance the location is chosen for: rural quiet of around 30 dB against the 55-75 dB of the city, without giving up access to it.'
			],
			whereLink: 'More about houses in Bistritsa',
			galleryAlt: 'The four houses of the Vkushty gated complex seen from above',
			ctaTitle: 'See the complex',
			ctaText: 'Request the blueprints and a 3D tour, watch the videos, or book an on-site viewing.',
			ctaDownload: 'Blueprints & 3D tour',
			ctaVideos: 'Videos',
				cta3d: 'Interactive 3D model',
			contactTitle: 'Book a viewing',
			contactText:
				'Fill in the form and we will get back to you to arrange an on-site viewing of the complex.'
		},
		bg: {
			title: 'Затворен Комплекс от Къщи в София — Комплексът Вкъщи, Бистрица',
			metaDescription:
				'Нов затворен комплекс от къщи в София: комплексът Вкъщи е четири къщи от 640-780 кв.м на ул. Изглед, Бистрица. Контролиран достъп, СОТ, камери и периметрови сензори, собствен парцел на къща, 5 минути от Околовръстното.',
			h1: 'Комплексът Вкъщи',
			subtitle: 'Затворен комплекс от само четири къщи над София',
			intro:
				'Комплексът Вкъщи е затворен комплекс от четири съвременни къщи на върха на ул. Изглед в Бистрица, София. Всяка къща е между 640 и 780 кв.м, разпределена на три нива, върху собствен парцел от 920-980 кв.м, зад един контролиран вход. Това е бутиков затворен комплекс, а не жилищен проект — четирима съседи, една улица и 360° панорама над града.',
			heroAlt: 'Въздушна снимка на затворения комплекс от четири къщи Вкъщи в Бистрица, София',
			factsTitle: 'Комплексът накратко',
			facts: [
				{ icon: faHouse, text: 'Само 4 къщи в целия комплекс' },
				{ icon: faShieldHalved, text: 'Затворен, със СОТ, камери и периметрови сензори' },
				{ icon: faVectorSquare, text: 'Собствен парцел от 920-980 кв.м на къща' },
				{ icon: faRulerVertical, text: '640-780 кв.м на три нива, тавани 3,39 м' },
				{ icon: faPersonSwimming, text: 'Отопляем басейн във всяка къща' },
				{ icon: faWarehouse, text: 'Гараж за 4 коли към всяка къща' },
				{ icon: faElevator, text: 'Асансьор KONE' },
				{ icon: faSolarPanel, text: 'Фотоволтаици и термопомпи' },
				{ icon: faRoad, text: '5 минути от Околовръстен път' }
			],
			whyTitle: 'Какво всъщност дава един затворен комплекс от къщи',
			whyParagraphs: [
				'Повечето затворени комплекси в София са жилищни сгради с бариера на входа. Затвореният комплекс от къщи е друго предложение: без общи стени, без общо входно антре, без асансьор, който да чакате. Общо е само това, което си струва да е общо — контролът на достъпа, периметърът, поддръжката на улицата.',
				'Мащабът има същото значение като портата. Комплекс от четири къщи означава четирима собственици, а не четиридесет — решенията за общите части са разговор, а не общо събрание, и улицата остава тиха, защото почти никой няма повод да мине по нея.',
				'Сигурността е на нива, а не символична: контролиран достъп на входа, СОТ, камери и периметрови сензори, като всяка къща стои в собствения си ограден парцел зад всичко това.'
			],
			housesTitle: 'Четирите къщи',
			housesParagraphs: [
				'Всяка къща е разпределена на три нива. На ниво -1 се намират гаражите, сервизните и мокрите помещения, както и просторни гардеробни. Основният етаж е активната част от дома — просторна дневна, кухня, офис и фитнес зона. Горният етаж е за отдих: мастър спалня с голям дрешник и детските стаи.',
				'Строителството е ново, с окачени алуминиеви фасади от Schüco, Германия, и тавани 3,39 м светла височина. Стъклените повърхности са това, около което е построен проектът: естествена светлина през целия ден и гледка към София практически от всяка стая.',
				'Всяка къща разполага със собствен отопляем басейн, покривна фотоволтаична система и термопомпи, върху парцел от 920-980 кв.м.'
			],
			whereTitle: 'Къде се намира комплексът',
			whereParagraphs: [
				'Улица „Изглед" е на върха на Бистрица, на 897 м по южните склонове на Витоша — над зимната инверсия, която задържа смога над по-голямата част от София от ноември до март. Панорамата се разгръща на изток и север над целия град, на запад и юг към планината.',
				'Въпреки това до Околовръстното са само 5 минути и точно заради този баланс е избрано мястото: селска тишина от около 30 dB срещу 55-75 dB в града, без да се отказвате от достъпа до него.'
			],
			whereLink: 'Повече за къщите в Бистрица',
			galleryAlt: 'Четирите къщи на затворения комплекс Вкъщи, гледани отгоре',
			ctaTitle: 'Разгледайте комплекса',
			ctaText: 'Заявете чертежите и 3D обиколка, гледайте видеата или запишете оглед на място.',
			ctaDownload: 'Чертежи и 3D тур',
			ctaVideos: 'Видеа',
				cta3d: 'Интерактивен 3D модел',
			contactTitle: 'Запишете оглед',
			contactText:
				'Попълнете формата и ще се свържем с вас, за да уговорим оглед на място в комплекса.'
		}
	};

	const t = languageEn ? text.en : text.bg;
	const pagePath = `${languageEn ? '/en' : ''}/kompleksat-vkushty`;
	const bistritsaPath = `${languageEn ? '/en' : ''}/kashti-bistritsa`;
	const downloadPath = `${languageEn ? '/en' : ''}/download`;
	const videosPath = `${languageEn ? '/en' : ''}/galeria`;

	return (
		<div className={styles.container}>
			<div id="snap-ready" style={{ display: 'none' }} />
			{/* defer={false} writes head tags synchronously — react-snap otherwise
			    races helmet's rAF-deferred update and can snapshot a bare <head>
			    (observed on /videos in the same build). */}
			<Helmet defer={false}>
				<html lang={languageEn ? 'en' : 'bg'} />
				<title>{t.title}</title>
				<meta name="description" content={t.metaDescription} />
				<link rel="canonical" href={`https://vkushty.com${pagePath}/`} />
				<meta property="og:title" content={t.title} />
				<meta property="og:description" content={t.metaDescription} />
				<meta property="og:url" content={`https://vkushty.com${pagePath}/`} />
				<meta property="og:type" content="website" />
				<meta property="og:image" content="https://vkushty.com/og-image.jpg" />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:image" content="https://vkushty.com/og-image.jpg" />
				<link rel="alternate" hreflang="bg" href="https://vkushty.com/kompleksat-vkushty/" />
				<link rel="alternate" hreflang="en" href="https://vkushty.com/en/kompleksat-vkushty/" />
				<link rel="alternate" hreflang="x-default" href="https://vkushty.com/kompleksat-vkushty/" />
				<meta property="og:locale" content={languageEn ? "en_US" : "bg_BG"} />
				<meta property="og:locale:alternate" content={languageEn ? "bg_BG" : "en_US"} />
				<script type="application/ld+json">
					{JSON.stringify({
						'@context': 'https://schema.org',
						'@type': 'RealEstateListing',
						name: languageEn
							? 'The Vkushty Complex - Gated Community of 4 Houses in Bistritsa, Sofia'
							: 'Комплексът Вкъщи - Затворен комплекс от 4 къщи в Бистрица, София',
						description: t.metaDescription,
						url: `https://vkushty.com${pagePath}/`,
						image: 'https://vkushty.com/og-image.jpg',
						numberOfRooms: '4',
						numberOfBathroomsTotal: 5,
						floorSize: {
							'@type': 'QuantitativeValue',
							value: '640-780',
							unitCode: 'MTK'
						},
						address: {
							'@type': 'PostalAddress',
							streetAddress: 'Izgled Street',
							addressLocality: 'Bistritsa',
							addressRegion: 'Sofia',
							postalCode: '1444',
							addressCountry: 'BG'
						},
						geo: {
							'@type': 'GeoCoordinates',
							latitude: 42.6095,
							longitude: 23.3785,
							elevation: '897'
						},
						additionalProperty: [
							{ '@type': 'PropertyValue', name: 'Houses in Complex', value: '4' },
							{ '@type': 'PropertyValue', name: 'Ceiling Height', value: '3.39m' },
							{ '@type': 'PropertyValue', name: 'Plot Size', value: '920-980 sq.m' },
							{ '@type': 'PropertyValue', name: 'Garage', value: '4-car' },
							{ '@type': 'PropertyValue', name: 'Gated', value: 'Yes' }
						]
					})}
				</script>
			</Helmet>

			<h1>{t.h1}</h1>
			<p className={styles.subtitle}>{t.subtitle}</p>

			<div className={styles.introSection}>
				<p>{t.intro}</p>
			</div>

			<img className={styles.heroImage} src={birdseye} alt={t.heroAlt} />

			<h2>{t.factsTitle}</h2>
			<div className={styles.featureGrid}>
				{t.facts.map((fact, index) => (
					<div key={index} className={styles.featureItem}>
						<div className={styles.featureIcon}>
							<FontAwesomeIcon icon={fact.icon} />
						</div>
						<p>{fact.text}</p>
					</div>
				))}
			</div>

			<h2>{t.whyTitle}</h2>
			{t.whyParagraphs.map((p, index) => (
				<p key={index} className={styles.sectionText}>
					{p}
				</p>
			))}

			<h2>{t.housesTitle}</h2>
			{t.housesParagraphs.map((p, index) => (
				<p key={index} className={styles.sectionText}>
					{p}
				</p>
			))}

			<img className={styles.heroImage} src={air2} alt={t.galleryAlt} loading="lazy" />

			<h2>{t.whereTitle}</h2>
			{t.whereParagraphs.map((p, index) => (
				<p key={index} className={styles.sectionText}>
					{p}
				</p>
			))}
			<p className={styles.sectionText}>
				<Link className={styles.inlineLink} to={bistritsaPath}>
					{t.whereLink}
				</Link>
			</p>

			<h2>{t.ctaTitle}</h2>
			<p className={styles.sectionText}>{t.ctaText}</p>
			<div className={styles.ctaRow}>
				<Link className={styles.ctaButton} to={downloadPath}>
					{t.ctaDownload}
				</Link>
				<Link className={styles.ctaButtonSecondary} to={videosPath}>
					{t.ctaVideos}
				</Link>
				<a className={styles.ctaButtonSecondary} href="/3d/ela-na-varha/">
					{t.cta3d}
				</a>
			</div>

			<h2>{t.contactTitle}</h2>
			<p className={styles.sectionText}>{t.contactText}</p>
			<div className={styles.contactWrapper}>
				<Contact languageEn={languageEn} />
			</div>
		</div>
	);
}

export default Kompleks;
